import { getTelemetryIdentity } from './telemetryIdentity';

type CdnFallbackUserStatus = 'anonymous' | 'free' | 'premium';
export type CdnFallbackStage = 'meta' | 'ref' | 'payload';

type CdnFallbackEvent = {
  surface: string;
  apiPath: string;
  reason: string;
  pagePath?: string;
  manifestPath?: string | null;
  refPath?: string | null;
  snapshotPath?: string | null;
  userStatus?: CdnFallbackUserStatus | null;
  isPremium?: boolean | null;
  stage?: CdnFallbackStage | null;
  statusCode?: number | null;
  details?: Record<string, unknown> | null;
  dedupeKey?: string;
};

const MIN_REPORT_GAP_MS = 60_000;
const RETENTION_MS = 10 * 60 * 1000;
const recentReports = new Map<string, number>();

const cleanupRecentReports = (now: number): void => {
  recentReports.forEach((timestamp, key) => {
    if (now - timestamp > RETENTION_MS) {
      recentReports.delete(key);
    }
  });
};

const resolvePagePath = (providedPagePath?: string): string => {
  if (providedPagePath) {
    return providedPagePath;
  }

  if (typeof window === 'undefined') {
    return 'unknown';
  }

  return `${window.location.pathname}${window.location.search}`;
};

export const reportCdnFallback = (event: CdnFallbackEvent): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const identity = getTelemetryIdentity();
  const pagePath = resolvePagePath(event.pagePath);
  const now = Date.now();
  cleanupRecentReports(now);

  const dedupeKey =
    event.dedupeKey ||
    [
      event.surface,
      event.apiPath,
      pagePath,
      event.stage || 'unknown',
      typeof event.statusCode === 'number' ? String(event.statusCode) : 'none',
      event.manifestPath || 'none',
      event.refPath || 'none',
      event.snapshotPath || 'none',
      identity.anonId || 'anon',
      identity.sessionId || 'session',
    ].join('|');

  const previousTimestamp = recentReports.get(dedupeKey) || 0;
  if (now - previousTimestamp < MIN_REPORT_GAP_MS) {
    return;
  }
  recentReports.set(dedupeKey, now);

  void fetch('/api/telemetry/cdn-fallback', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    keepalive: true,
    body: JSON.stringify({
      surface: event.surface,
      apiPath: event.apiPath,
      reason: event.reason,
      pagePath,
      manifestPath: event.manifestPath || null,
      refPath: event.refPath || null,
      snapshotPath: event.snapshotPath || null,
      userStatus: event.userStatus || null,
      isPremium: typeof event.isPremium === 'boolean' ? event.isPremium : null,
      stage: event.stage || null,
      statusCode: typeof event.statusCode === 'number' ? event.statusCode : null,
      details: event.details || null,
      anonId: identity.anonId,
      sessionId: identity.sessionId,
      site: identity.site,
      userAgent: navigator.userAgent || null,
    }),
  }).catch((error) => {
    console.warn('Failed to send CDN fallback telemetry.', error);
  });
};
